import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useChannelCounts } from '../../modules/communicatie/hooks/useCommunicatie.js';
import { useSocketEvent } from '../shared/hooks/useSocket.js';
import { useAuth } from '../auth/AuthContext.js';
import { useModuleLabels } from '../shared/hooks/useModuleLabels.js';
import { KIcon } from '../shared/components/KIcon.js';
import { MerkLogo } from './MerkLogo.js';
import { navigatieVoorRol, isActiefPad, type NavDoel } from './navigatie.js';

/** Bestaande tellers: ongelezen gesprekken (API) en ongeziene logistieke updates (socket). */
export function useNavBadges() {
  const location = useLocation();
  const { data: counts } = useChannelCounts();
  const [logistiek, setLogistiek] = useState(0);
  const opLogistiek = location.pathname.startsWith('/logistiek');
  const handle = useCallback(() => { if (!opLogistiek) setLogistiek((n) => n + 1); }, [opLogistiek]);
  useSocketEvent('logistiek:update', handle);
  useEffect(() => { if (opLogistiek) setLogistiek(0); }, [opLogistiek]);
  return (doel: NavDoel): number => {
    if (doel.badge === 'logistiek') return logistiek;
    if (doel.badge === 'gesprekken') return Number((counts as { total?: number } | undefined)?.total) || 0;
    return 0;
  };
}

export function useNavigatie() {
  const { user } = useAuth();
  const labels = useModuleLabels();
  return useMemo(() => navigatieVoorRol(user?.role, {
    communicatie: labels.communicatie.label,
    crm: labels.crm.label,
    buitendienst: labels.buitendienst.label,
    logistiek: labels.logistiek.label,
    planning: labels.planning.label,
  }), [user?.role, labels]);
}

function NavRegel({ doel, aantal, onNavigeer }: { doel: NavDoel; aantal: number; onNavigeer?: () => void }) {
  const location = useLocation();
  const actief = isActiefPad(location.pathname, doel.pad);
  return (
    <Link to={doel.pad} onClick={onNavigeer} aria-current={actief ? 'page' : undefined} className="k-nav-link">
      <KIcon naam={doel.icoon} />
      <span className="min-w-0 truncate">{doel.label}</span>
      {aantal > 0 && (
        <span className="k-nav-badge">
          {aantal > 99 ? '99+' : aantal}
          <span className="k-sr-only"> {doel.badge === 'logistiek' ? 'nieuwe updates' : 'ongelezen'}</span>
        </span>
      )}
    </Link>
  );
}

/**
 * Lichte zijbalk (Kordaro v4): 240 px vanaf 1200, 208 px van 1024 tot 1199; daaronder als
 * inklapbaar menu (zelfde inhoud) via de knop in de kopbalk.
 */
export function Sidebar({ variant = 'vast', onNavigeer, onSluit }: {
  variant?: 'vast' | 'lade';
  onNavigeer?: () => void;
  onSluit?: () => void;
}) {
  const nav = useNavigatie();
  const badge = useNavBadges();
  const lijst = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (variant === 'lade') lijst.current?.querySelector<HTMLElement>('a')?.focus();
  }, [variant]);

  return (
    <aside
      aria-label="Hoofdnavigatie"
      className={clsx('k-sidebar h-full', variant === 'vast' ? 'k-sidebar--vast' : 'w-[280px] max-w-[85vw]')}
    >
      <div className="flex items-center justify-between gap-2 px-2 pt-2 pb-6">
        <MerkLogo breedte={160} />
        {variant === 'lade' && (
          <button type="button" onClick={onSluit} className="k-button k-button--icon" aria-label="Menu sluiten">
            <KIcon naam="sluit" />
          </button>
        )}
      </div>
      <div ref={lijst} className="flex flex-col gap-1">
        {nav.hoofd.map((d) => <NavRegel key={d.pad} doel={d} aantal={badge(d)} onNavigeer={onNavigeer} />)}
      </div>
      <div className="flex-1" />
      {nav.onder.length > 0 && (
        <div className="flex flex-col gap-1 pt-3 border-t border-card-border">
          {nav.onder.map((d) => <NavRegel key={d.pad} doel={d} aantal={0} onNavigeer={onNavigeer} />)}
        </div>
      )}
    </aside>
  );
}
